import React, { useState, useCallback } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, TextField, FormControl, InputLabel, Select,
  MenuItem, Button, CircularProgress, Alert, Pagination, Tooltip
} from '@mui/material';
import { History as HistoryIcon, Search as SearchIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { contentService } from '../services/api';

const ACTION_COLOR = { CREATE: 'success', STATUS_CHANGE: 'primary', UPDATE: 'default', DELETE: 'error' };

export default function ContentAuditLog() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);

  const [filters, setFilters] = useState({
    contentId: '',
    action: '',
    changedBy: '',
    startDate: '',
    endDate: '',
  });

  const loadLogs = useCallback(async (pg = 1) => {
    setLoading(true);
    setError('');
    try {
      const params = { page: pg, limit: 50 };
      if (filters.contentId) params.contentId = filters.contentId;
      if (filters.action)    params.action    = filters.action;
      if (filters.changedBy) params.changedBy = filters.changedBy;
      if (filters.startDate) params.startDate = filters.startDate;
      if (filters.endDate)   params.endDate   = filters.endDate;

      const res = await contentService.getAllAuditLogs(params);
      setLogs(res.data.logs || []);
      setTotal(res.data.total || 0);
      setPage(pg);
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const handleSearch = () => loadLogs(1);
  const handleReset = () => {
    setFilters({ contentId: '', action: '', changedBy: '', startDate: '', endDate: '' });
    setLogs([]);
    setTotal(0);
    setPage(1);
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1300, mx: 'auto' }}>
      <Box display="flex" alignItems="center" gap={1.5} mb={3}>
        <HistoryIcon sx={{ fontSize: 30, color: 'primary.main' }} />
        <Box>
          <Typography variant="h5" fontWeight={700}>Content Audit Log</Typography>
          <Typography variant="body2" color="text.secondary">Complete change history across all content</Typography>
        </Box>
      </Box>

      {/* Filters */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Box display="flex" gap={2} flexWrap="wrap" alignItems="flex-end">
          <TextField size="small" label="Content ID" value={filters.contentId}
            onChange={e => setFilters(p => ({ ...p, contentId: e.target.value }))}
            placeholder="e.g. SCE-LV-290526-001" sx={{ minWidth: 200 }} />
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Action</InputLabel>
            <Select value={filters.action} label="Action"
              onChange={e => setFilters(p => ({ ...p, action: e.target.value }))}>
              <MenuItem value="">All</MenuItem>
              <MenuItem value="CREATE">Create</MenuItem>
              <MenuItem value="STATUS_CHANGE">Status Change</MenuItem>
              <MenuItem value="UPDATE">Update</MenuItem>
              <MenuItem value="DELETE">Delete</MenuItem>
            </Select>
          </FormControl>
          <TextField size="small" label="Changed By" value={filters.changedBy}
            onChange={e => setFilters(p => ({ ...p, changedBy: e.target.value }))}
            placeholder="Username" sx={{ minWidth: 160 }} />
          <TextField size="small" label="From Date" type="date" value={filters.startDate}
            onChange={e => setFilters(p => ({ ...p, startDate: e.target.value }))}
            InputLabelProps={{ shrink: true }} />
          <TextField size="small" label="To Date" type="date" value={filters.endDate}
            onChange={e => setFilters(p => ({ ...p, endDate: e.target.value }))}
            InputLabelProps={{ shrink: true }} />
          <Button variant="contained" startIcon={<SearchIcon />} onClick={handleSearch}>
            Search
          </Button>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={handleReset}>
            Reset
          </Button>
        </Box>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>
      ) : logs.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
          <Typography color="text.secondary">
            {total === 0 && !loading ? 'Use the filters above and click Search to load logs.' : 'No logs found.'}
          </Typography>
        </Paper>
      ) : (
        <>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
            {total} total entries
          </Typography>
          <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'action.hover' }}>
                  <TableCell sx={{ fontWeight: 700 }}>Time</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Content ID</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Title</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Action</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Changed By</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Role</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Details</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {logs.map(log => (
                  <TableRow key={log.id} hover>
                    <TableCell sx={{ whiteSpace: 'nowrap', fontSize: '0.75rem' }}>
                      {new Date(log.createdAt).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })}
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                      {log.contentId || '—'}
                    </TableCell>
                    <TableCell sx={{ maxWidth: 200 }}>
                      <Tooltip title={log.contentTitle || ''}>
                        <Typography variant="caption" sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {log.contentTitle || '—'}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Chip label={log.action} size="small" color={ACTION_COLOR[log.action] || 'default'} />
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.8rem' }}>{log.changedByName || '—'}</TableCell>
                    <TableCell>
                      <Chip label={log.changedByRole || '—'} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.75rem', maxWidth: 260 }}>
                      {log.action === 'STATUS_CHANGE'
                        ? <span><b>{log.oldStatus}</b> → <b>{log.newStatus}</b></span>
                        : log.changedFields?.length
                          ? <Tooltip title={log.changedFields.join(', ')}>
                              <span>{log.changedFields.slice(0, 3).join(', ')}{log.changedFields.length > 3 ? ` +${log.changedFields.length - 3} more` : ''}</span>
                            </Tooltip>
                          : '—'
                      }
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {total > 50 && (
            <Box mt={2} display="flex" justifyContent="center">
              <Pagination count={Math.ceil(total / 50)} page={page}
                onChange={(_, p) => loadLogs(p)} color="primary" />
            </Box>
          )}
        </>
      )}
    </Box>
  );
}
